import React, { FC, useEffect, useState, useCallback, useRef } from "react";
import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
  StyleSheet,
} from "@react-pdf/renderer";

import moment from "moment";
// Register fonts to fix loading issues
Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVjJx26TKEr37c9WBI.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVmJx26TKEr37c9YL5rulw.ttf",
});

// ใช้ NBSP+ZWSP ต่อท้ายข้อความไทยใน PDF เพื่อกัน react-pdf clip ตัวสุดท้าย
// NBSP มี width จริง ดันตัวอักษรออกจาก border ส่วน ZWSP เปิดให้ break บรรทัดได้
const zw = "\u00A0\u200B";

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const ReportRetirementTrendNext3YearsListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
    year: moment().year() + 543,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };

  const headers: Header[] = [
    {
      label: "ที่",
      field: "index",
      order: null,
      isOrder: true,
    },
    {
      label: "หน่วยงาน/ตำแหน่งทางสายงาน",
      field: "name",
      order: null,
      isOrder: true,
    },
    {
      label: "กรอบอัตรากำลัง",
      field: "main_power_rate",
      order: null,
      isOrder: true,
    },
    {
      label: "อัตราว่าง",
      field: "vacant_positions",
      order: null,
      isOrder: true,
    },
    {
      label: "บรรจุจริง",
      field: "actual_filled",
      order: null,
      isOrder: true,
    },
    {
      label: (moment().year() + 543).toString(),
      field: "retire_current",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 544),
      field: "retire_next1",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 545),
      field: "retire_next2",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result =
          await HrOnlineService.getReportRetirementTrendNext3YearsList(params);
        const resultAll =
          await HrOnlineService.getReportRetirementTrendNext3YearsList({
            ...params,
            page_size: 10000,
            page: 1,
          });
        setData(result);
        setDataExport(resultAll);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message,
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering_field, queryParams.ordering],
  );

  const tableRowNoData = () => {
    if (dataExport.rows?.length === 0) {
      return <TableRowNoData colspan={9} />;
    }
    return null;
  };

  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData, queryParams]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">
          รายงานแบ่งประเภท : แนวโน้มการเกษียณอายุของตำแหน่ง แยกตามส่วนงาน 3
          ปีข้างหน้า
        </h2>
      </div>
      {loaderComponent}
      <div className="row">
        {/* <label className="form-label">ปี</label>
        <div className="col-sm-12 col-md-2">
          <input
            className="form-control"
            type="text"
            value={queryParams.year ? queryParams.year : ""}
            onChange={handleYearChange}
            name="search"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Button
            variant="primary"
            type="button"
            className="d-inline-block px-4"
            onClick={() => fetchData(queryParams)}
          >
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </Button>
        </div> */}
        <div className="col-sm-12 col-md-12 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานผู้เกษียณอายุในปี_${queryParams.year}.pdf`}
          >
            {
              (({ loading }: any) => (
                <Button
                  variant="primary"
                  className="btn-action btn-mof-primary "
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  {loading ? "Loading..." : "Export"}
                </Button>
              )) as any
            }
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {dataExport.rows?.map(
              (department: any, departmentIndex: number) => (
                <React.Fragment key={`dept-${departmentIndex}`}>
                  {/* Department row */}
                  <ReportRetirementTrendNext3YearsRow
                    {...department}
                    index={departmentIndex + 1}
                    isParentRow={true}
                  />

                  {/* Division Groups */}
                  {department.division_group &&
                    typeof department.division_group === "object" &&
                    Object.keys(department.division_group).map(
                      (divKey: string, divIndex: number) => {
                        const divisionData = department.division_group[divKey];
                        return divisionData ? (
                          <React.Fragment
                            key={`div-${departmentIndex}-${divIndex}`}
                          >
                            {/* Division row */}
                            <ReportRetirementTrendNext3YearsRow
                              {...divisionData}
                              index={`${departmentIndex + 1}.${divIndex + 1}`}
                              isParentRow={true}
                            />

                            {/* Position rows under division */}
                            {divisionData?.positions &&
                              Array.isArray(divisionData.positions) &&
                              divisionData.positions.map(
                                (position: any, posIndex: number) => (
                                  <ReportRetirementTrendNext3YearsRow
                                    key={`pos-${departmentIndex}-${divIndex}-${posIndex}`}
                                    {...position}
                                    index={posIndex + 1}
                                    isParentRow={false}
                                  />
                                ),
                              )}

                            {/* Summary for division */}
                            {divisionData?.summary && (
                              <ReportRetirementTrendNext3YearsRow
                                {...divisionData.summary}
                                index={`${departmentIndex + 1}.${divIndex + 1}`}
                                type={`${
                                  divisionData?.division_group_name || ""
                                }`}
                                isParentRow={false}
                              />
                            )}
                          </React.Fragment>
                        ) : null;
                      },
                    )}

                  {/* Positions directly under department (if any) */}
                  {department.positions &&
                    Array.isArray(department.positions) &&
                    department.positions.map(
                      (position: any, posIndex: number) => (
                        <ReportRetirementTrendNext3YearsRow
                          key={`pos-${departmentIndex}-direct-${posIndex}`}
                          {...position}
                          index={posIndex + 1}
                          isParentRow={false}
                        />
                      ),
                    )}

                  {/* Department summary */}
                  {department.summary && !department.division_group && (
                    <ReportRetirementTrendNext3YearsRow
                      {...department.summary}
                      index={`${departmentIndex + 1}`}
                      type={`${
                        department.department_office_name ||
                        department.division_group_name ||
                        department.manager_name ||
                        ""
                      }`}
                      isParentRow={false}
                    />
                  )}
                </React.Fragment>
              ),
            )}

            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const ReportRetirementTrendNext3YearsRow: FC<any> = ({
  index,
  structure_position_name,
  division_group_name,
  department_office_name,
  manager_name,
  allocated_power_rate,
  actual_filled,
  retire_current,
  retire_next1,
  retire_next2,
  isParentRow,
  type = null,
}) => {
  // Get the display name based on hierarchy level
  const getDisplayName = () => {
    if (typeof type === "string") return type;
    if (structure_position_name) return structure_position_name;
    if (division_group_name) return division_group_name;
    if (department_office_name) return department_office_name;
    if (manager_name) return manager_name;
    return "";
  };

  // Determine if this is a parent row (department or division) if not explicitly set
  const isParentRowFinal =
    isParentRow !== undefined
      ? isParentRow
      : !structure_position_name &&
        (department_office_name || division_group_name || manager_name);

  return (
    <tr>
      <td>{type || isParentRowFinal ? "" : index}</td>
      <td>{getDisplayName()}</td>
      <td>{!isParentRowFinal ? allocated_power_rate || 0 : ""}</td>
      <td>
        {!isParentRowFinal
          ? (allocated_power_rate || 0) - (actual_filled || 0)
          : ""}
      </td>
      <td>{!isParentRowFinal ? actual_filled || 0 : ""}</td>
      <td>{!isParentRowFinal ? retire_current || "0" : ""}</td>
      <td>{!isParentRowFinal ? retire_next1 || "0" : ""}</td>
      <td>{!isParentRowFinal ? retire_next2 || "0" : ""}</td>
    </tr>
  );
};

// PDF Component for export
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "kanit-regular",
  },
  header: {
    fontSize: 13,
    marginBottom: 5,
    textAlign: "center",
    fontFamily: "kanit-regular",
  },
  headerTitle: {
    fontSize: 16,
    marginBottom: 1,
    textAlign: "center",
    fontFamily: "kanit-regular",
  },
  table: {
    border: "1px solid black",
  },
  tableRow: {
    flexDirection: "row",
  },
  /* ความกว้างคอลัมน์เดียวกันทั้งหัวและตัวตาราง (รวม 100%) */
  tableCol1: {
    width: "7%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-200",
  },
  tableCol2: {
    width: "25%",
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 5,
    paddingRight: 10,
    fontSize: 10,
    textAlign: "left",
    borderRight: "1px solid black",
    fontFamily: "kanit-200",
  },
  tableCol3: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-200",
  },
  tableCol4: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-200",
  },
  tableCol5: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-200",
  },
  tableCol6: {
    width: "10%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-200",
  },
  tableCol7: {
    width: "10%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-200",
  },
  tableCol8: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    fontFamily: "kanit-200",
  },
  tableColSummary1: {
    width: "7%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColSummary2: {
    width: "25%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColSummary3: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColSummary4: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColSummary5: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColSummary6: {
    width: "10%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColSummary7: {
    width: "10%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColSummary8: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    fontFamily: "kanit-regular",
  },
  tableColHeader1: {
    width: "7%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColHeader2: {
    width: "25%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColHeader3: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColHeader4: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColHeader5: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColHeader6: {
    width: "10%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColHeader7: {
    width: "10%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRight: "1px solid black",
    fontFamily: "kanit-regular",
  },
  tableColHeader8: {
    width: "12%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    fontFamily: "kanit-regular",
  },
  footer: {
    marginTop: 20,
    fontSize: 10,
    textAlign: "right",
  },
  parentRow: {
    borderBottom: "1px solid black",
  },
  summaryRow: {
    borderBottom: "1px solid black",
    fontWeight: "bold",
  },
  headerRow: {
    borderBottom: "1px solid black",
  },
  headerRowFixed: {
    flexDirection: "row" as const,
    border: "1px solid black",
    fontFamily: "kanit-200",
    fontSize: 10,
    backgroundColor: "white",
  },
  dataRow: {
    borderBottom: "1px solid black",
  },
});

const PDF: FC<{ data: any }> = ({ data }) => {
  // Format current date in Thai
  const thaiDate =
    moment().format("D") +
    " " +
    thaiMonths[moment().month()] +
    " " +
    (moment().year() + 543);
  const currentYear = moment().year() + 543;

  // Log data for debugging
  console.log("PDF Data:", data);

  // Helper to check if an item is a department
  const isDepartment = (item: any) => {
    return item && item.department_office_name && !item.structure_position_name;
  };

  // Helper to check if an item is a division
  const isDivision = (item: any) => {
    return item && item.division_group_name && !item.structure_position_name;
  };

  // Helper to process data into a flat array for rendering
  const processData = () => {
    if (!data) return [];

    // Check if data is already a flat array
    if (Array.isArray(data) && data.length > 0 && !data[0].rows) {
      return data;
    }

    // If data is in hierarchical structure with rows property
    const flatData: any[] = [];

    if (data.rows && Array.isArray(data.rows)) {
      data.rows.forEach((department: any, deptIndex: number) => {
        // Add department
        if (department.department_office_name || department.manager_name) {
          flatData.push({
            ...department,
            isParent: true,
            index: deptIndex + 1,
            type: "department",
          });
        }

        // Add positions directly under department
        if (department.positions && Array.isArray(department.positions)) {
          department.positions.forEach((position: any, posIndex: number) => {
            flatData.push({
              ...position,
              index: `${posIndex + 1}`,
              isParent: false,
              type: "position",
            });
          });
        }

        // Add department summary if available
        if (department.summary && !department.division_group) {
          flatData.push({
            ...department.summary,
            structure_position_name:
              department.department_office_name ||
              department.manager_name ||
              "",
            index: deptIndex + 1,
            isParent: false,
            type: "summary",
          });
        }

        // Process division groups
        if (
          department.division_group &&
          typeof department.division_group === "object"
        ) {
          Object.keys(department.division_group).forEach(
            (divKey: string, divIndex: number) => {
              const division = department.division_group[divKey];

              if (division) {
                // Add division
                flatData.push({
                  ...division,
                  isParent: true,
                  index: `${divIndex + 1}`,
                  type: "division",
                });

                // Add positions under division
                if (division.positions && Array.isArray(division.positions)) {
                  division.positions.forEach(
                    (position: any, posIndex: number) => {
                      flatData.push({
                        ...position,
                        index: `${posIndex + 1}`,
                        isParent: false,
                        type: "position",
                      });
                    },
                  );
                }

                // Add division summary
                if (division.summary) {
                  flatData.push({
                    ...division.summary,
                    structure_position_name: division.division_group_name || "",
                    index: `${divIndex + 1}`,
                    isParent: false,
                    type: "summary",
                  });
                }
              }
            },
          );
        }
      });
    }

    return flatData.length > 0 ? flatData : Array.isArray(data) ? data : [];
  };

  const processedData = processData();

  // คำนวณแถวรวมจากแถว position เท่านั้น (ไม่รวม summary เพื่อไม่ double count)
  const totalRow = processedData.reduce(
    (acc, item) => {
      if (item.type !== "position") return acc;
      const allocated = parseFloat(
        item.allocated_power_rate || item.main_power_rate || "0",
      );
      const actual = parseFloat(item.actual_filled || "0");
      return {
        allocated: acc.allocated + allocated,
        actual: acc.actual + actual,
        vacant: acc.vacant + (allocated - actual),
        retire_current:
          acc.retire_current + parseFloat(item.retire_current || "0"),
        retire_next1: acc.retire_next1 + parseFloat(item.retire_next1 || "0"),
        retire_next2: acc.retire_next2 + parseFloat(item.retire_next2 || "0"),
      };
    },
    {
      allocated: 0,
      actual: 0,
      vacant: 0,
      retire_current: 0,
      retire_next1: 0,
      retire_next2: 0,
    },
  );

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap>
        <View>
          {/* Organization Header */}
          <Text style={styles.headerTitle}>
            {"องค์การตลาดเพื่อเกษตรกร" + zw}
          </Text>

          {/* Report Title */}
          <Text style={styles.headerTitle}>
            {"รายงานแบ่งประเภท : แนวโน้มการเกษียณอายุของตำแหน่ง แยกตามส่วนงาน 3 ปีข้างหน้า" +
              zw}
          </Text>

          {/* Current Date */}
          <Text style={styles.headerTitle}>{"ประจำวันที่ " + thaiDate + zw}</Text>
        </View>

        {/* หัวตาราง - ใช้ความกว้างคอลัมน์เดียวกับแถวข้อมูล (7+25+12+12+12+10+10+12=100%) */}
        <View style={styles.headerRowFixed} fixed>
          <View style={styles.tableColHeader1}>
            <Text>{"ที่" + zw}</Text>
          </View>
          <View style={styles.tableColHeader2}>
            <Text>{"หน่วยงาน/ตำแหน่งทางสายงาน" + zw}</Text>
          </View>
          <View style={styles.tableColHeader3}>
            <Text>{"กรอบอัตรากำลัง" + zw}</Text>
          </View>
          <View style={styles.tableColHeader4}>
            <Text>{"อัตราว่าง" + zw}</Text>
          </View>
          <View style={styles.tableColHeader5}>
            <Text>{"บรรจุจริง" + zw}</Text>
          </View>
          <View style={styles.tableColHeader6}>
            <Text>{currentYear + zw}</Text>
          </View>
          <View style={styles.tableColHeader7}>
            <Text>{currentYear + 1 + zw}</Text>
          </View>
          <View style={styles.tableColHeader8}>
            <Text>{currentYear + 2 + zw}</Text>
          </View>
        </View>

        {/* ตัวตาราง - border ต่อจากหัว (borderTop: none เพื่อต่อกับหัว) */}
        <View style={[styles.table, { borderTop: "none" }]}>
          {/* Table Data */}
          {processedData.map((item: any, index: number) => {
            const isParent =
              item.isParent || isDepartment(item) || isDivision(item);
            const isSummary = item.type === "summary";

            const displayName =
              item.structure_position_name ||
              item.division_group_name ||
              item.department_office_name ||
              item.manager_name ||
              "";

            // Get allocated and actual rates with fallbacks.
            // ใช้ ?? แทน || เพราะค่า 0 (number) ใน summary จะถูกตีความว่า falsy
            // ทำให้ "0 || \"\"" กลายเป็น "" ซึ่งทำให้คอลัมน์ตัวเลขแสดงว่างเปล่า
            const allocatedRate =
              item.allocated_power_rate ?? item.main_power_rate ?? "";
            const actualRate = item.actual_filled ?? "";
            const vacantPositions =
              allocatedRate !== "" && actualRate !== ""
                ? (
                    parseFloat(String(allocatedRate)) -
                    parseFloat(String(actualRate))
                  ).toString()
                : "";

            return (
              <View
                style={[
                  styles.tableRow,
                  isSummary ? styles.summaryRow : styles.dataRow,
                ]}
                key={`item-${index}`}
                wrap={false}
              >
                <View style={styles.tableCol1}>
                  <Text>
                    {item.type === "summary" || isParent ? "" : item.index}
                  </Text>
                </View>
                <View style={styles.tableCol2}>
                  <Text>{displayName + zw}</Text>
                </View>
                <View style={styles.tableCol3}>
                  <Text>{!isParent || isSummary ? allocatedRate : ""}</Text>
                </View>
                <View style={styles.tableCol4}>
                  <Text>{!isParent || isSummary ? vacantPositions : ""}</Text>
                </View>
                <View style={styles.tableCol5}>
                  <Text>{!isParent || isSummary ? actualRate : ""}</Text>
                </View>
                <View style={styles.tableCol6}>
                  <Text>
                    {!isParent || isSummary ? item.retire_current || "0" : ""}
                  </Text>
                </View>
                <View style={styles.tableCol7}>
                  <Text>
                    {!isParent || isSummary ? item.retire_next1 || "0" : ""}
                  </Text>
                </View>
                <View style={styles.tableCol8}>
                  <Text>
                    {!isParent || isSummary ? item.retire_next2 || "0" : ""}
                  </Text>
                </View>
              </View>
            );
          })}

          {/* แถวรวมท้ายตาราง - ใช้ความกว้างคอลัมน์เดียวกับหัวและแถวข้อมูล */}
          <View style={[styles.tableRow, styles.summaryRow]} wrap={false}>
            <View style={styles.tableColSummary1}>
              <Text></Text>
            </View>
            <View style={styles.tableColSummary2}>
              <Text>{"รวม" + zw}</Text>
            </View>
            <View style={styles.tableColSummary3}>
              <Text>{totalRow.allocated}</Text>
            </View>
            <View style={styles.tableColSummary4}>
              <Text>{totalRow.vacant}</Text>
            </View>
            <View style={styles.tableColSummary5}>
              <Text>{totalRow.actual}</Text>
            </View>
            <View style={styles.tableColSummary6}>
              <Text>{totalRow.retire_current}</Text>
            </View>
            <View style={styles.tableColSummary7}>
              <Text>{totalRow.retire_next1}</Text>
            </View>
            <View style={styles.tableColSummary8}>
              <Text>{totalRow.retire_next2}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ReportRetirementTrendNext3YearsListPage;
